import React, { type ReactNode } from 'react';
import { styled } from '@compiled/react';
import { ButtonGroup } from '@atlaskit/button';
import { LinkButton } from '@atlaskit/button/new';
import { Flex, Inline, Stack } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import { fg } from '@atlassian/jira-feature-gating';
import HelpPanelButton from '@atlassian/jira-help-panel-button/src/index.tsx';
import { useIntl } from '@atlassian/jira-intl';
import {
	ContextualAnalyticsData,
	SCREEN,
	type UIAnalyticsEvent,
	FireScreenAnalytics,
	fireUIAnalytics,
} from '@atlassian/jira-product-analytics-bridge';
import CreateComponentButton from '../../create-component-button/index.tsx';
import { EmptyStateImage, EmptyStateBlocks } from './assets/index.tsx';
import messages from './messages.tsx';

const ISSUES_URL = (projectKey: string) =>
	// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
	encodeURI(`${window.location.origin}/browse/${projectKey}/issues`);

export function ComponentsTableEmptyState({
	loading,
	projectKey,
	testId,
	componentCount,
	displayMode,
}: {
	testId?: string;
	loading: boolean;
	projectKey: string;
	componentCount?: number;
	displayMode: 'no-components' | 'no-linked-components' | 'unknown';
}) {
	const { formatMessage } = useIntl();
	const createButtonTestId = testId && `${testId}-create-button`;

	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	const onClickLearnMore = (_: any, analyticEvent: UIAnalyticsEvent) => {
		fireUIAnalytics(analyticEvent, 'compassComponentsTableEmptyStateLearnMore');
	};

	const EmptyState = displayMode === 'no-linked-components' ? EmptyStateImage : EmptyStateBlocks;

	const primaryAction =
		displayMode === 'no-linked-components' ? (
			<LinkButton appearance="primary" href={ISSUES_URL(projectKey)}>
				{formatMessage(
					fg('jira-issue-terminology-refresh-m3')
						? messages.gotoIssuesIssueTermRefresh
						: messages.gotoIssues,
				)}
			</LinkButton>
		) : (
			<CreateComponentButton
				testId={createButtonTestId}
				loading={loading}
				// eslint-disable-next-line @typescript-eslint/no-empty-function
				oldOnAddComponent={() => {}}
				projectKey={projectKey}
				globalComponentsEnabled
				verbose
			/>
		);

	let heading;
	let body;

	switch (displayMode) {
		case 'no-components':
			heading = messages.noComponentsHeading;
			body = fg('jira-issue-terminology-refresh-m3')
				? messages.noComponentsBodyIssueTermRefresh
				: messages.noComponentsBody;
			break;

		case 'no-linked-components':
			heading = fg('jira-issue-terminology-refresh-m3')
				? messages.noLinkedComponentsHeadingIssueTermRefresh
				: messages.noLinkedComponentsHeading;
			body = fg('jira-issue-terminology-refresh-m3')
				? messages.noLinkedComponentsBodyIssueTermRefresh
				: messages.noLinkedComponentsBody;
			break;

		case 'unknown':
			heading = messages.heading;
			body = fg('jira-issue-terminology-refresh-m3')
				? messages.noComponentsBodyIssueTermRefresh
				: messages.noComponentsBody;
			break;

		default:
			heading = messages.heading;
			body = fg('jira-issue-terminology-refresh-m3')
				? messages.noComponentsBodyIssueTermRefresh
				: messages.noComponentsBody;
			break;
	}

	return (
		<ContextualAnalyticsData sourceType={SCREEN} sourceName="componentsTableEmptyState">
			<FireScreenAnalytics eventName="viewed componentsTableEmptyState" />
			<Container data-testid={testId}>
				<Inline space="space.400" alignBlock="center">
					<EmptyState />
					<Stack alignInline="start">
						<Heading>{formatMessage(heading, { componentCount })}</Heading>
						<Body>
							{formatMessage(body, {
								b: (chunks: ReactNode) => <b>{chunks}</b>,
							})}
						</Body>
						<ButtonGroup>
							<Flex gap="space.200" alignItems="center">
								{primaryAction}

								<HelpPanelButton
									articleId="6frUzedhV1M0c05JhXMvK0"
									appearance="link"
									spacing="none"
									onClick={onClickLearnMore}
									fallbackComponent={
										<LinkButton
											href="https://support.atlassian.com/jira-software-cloud/docs/link-issues-to-compass-components/"
											onClick={onClickLearnMore}
											target="_blank"
										>
											{formatMessage(messages.learnMore)}
										</LinkButton>
									}
								>
									{formatMessage(messages.learnMore)}
								</HelpPanelButton>
							</Flex>
						</ButtonGroup>
					</Stack>
				</Inline>
			</Container>
		</ContextualAnalyticsData>
	);
}

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Container = styled.div({
	marginTop: token('space.1000'),
	marginRight: 'auto',
	marginBottom: token('space.1000'),
	marginLeft: 'auto',
	width: 702,
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Heading = styled.div({
	font: token('font.heading.small'),
	marginBottom: token('space.200'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Body = styled.div({
	marginBottom: token('space.300'),
	width: '460px',
});
